/**
 * Parse a subscription date field that may be a Firestore Timestamp (client or
 * admin SDK serialisation), a plain JS Date, or an ISO string.
 * @param {*} value
 * @returns {Date|null}
 */
const parseSubDate = (value) => {
  if (!value) return null;
  // Firestore client Timestamp object (has toDate method)
  if (typeof value.toDate === "function") return value.toDate();
  // Serialised client SDK Timestamp: { seconds, nanoseconds }
  if (value.seconds != null) return new Date(value.seconds * 1000);
  // Serialised admin SDK Timestamp: { _seconds, _nanoseconds }
  if (value._seconds != null) return new Date(value._seconds * 1000);
  // ISO string or numeric timestamp
  const d = new Date(value);
  return isNaN(d.getTime()) ? null : d;
};

/**
 * @param {Object} user
 * @param {string} contentType
 * @param {string} [classType=null]
 * @returns {Object}
 */
export const checkAccess = (user, contentType, classType = null) => {
  // Check for free trial access first
  if (user?.freeAccess === true) {
    return { hasAccess: true, reason: "Free trial access" };
  }

  // Universal credit check - if user has credits, they can join any class
  if (user?.credits > 0) {
    return { hasAccess: true, reason: "Available credits" };
  }

  // Always allow free access to Group Standard classes
  if (classType === "Group Standard") {
    return { hasAccess: true, reason: "Group Standard classes are free" };
  }

  // Check for Immersive Exam Prep plan (allows all premium groups)
  const hasExamPrepPlan = user?.subscriptions?.some((sub) => {
    if (
      !sub.startDate ||
      !sub.endDate ||
      sub.type === "None" ||
      sub.type === "none"
    )
      return false;
    const endDate = parseSubDate(sub.endDate);
    const startDate = parseSubDate(sub.startDate);
    if (!endDate || !startDate) return false;
    const now = new Date();
    const type = sub.type.trim().toLowerCase();
    return (
      endDate > now && startDate <= now &&
      (type === "immersive exam prep" || type === "exam_prep")
    );
  });

  // Check for bammbuu+ group class subscription (allows premium group classes).
  // Matches the same broad set of subscription types used in Sidebar and ProfileUser.
  const hasGroupSubscription = user?.subscriptions?.some((sub) => {
    if (
      !sub.startDate ||
      !sub.endDate ||
      !sub.type ||
      sub.type.toLowerCase() === "none"
    )
      return false;
    const endDate = parseSubDate(sub.endDate);
    const startDate = parseSubDate(sub.startDate);
    if (!endDate || !startDate) return false;
    const now = new Date();
    const type = sub.type.trim().toLowerCase();
    // Exact known type strings
    const isKnownGroupType =
      type === "bammbuu+ instructor-led group classes" ||
      type === "group_premium" ||
      type === "bammbuu groups";
    // Broad fallback: any type that signals a bammbuu+ group plan but is NOT
    // a pure individual/private plan (guards against "individual_premium" etc.)
    const isBroadGroupType =
      (type.includes("bammbuu+") || type.includes("group")) &&
      !type.includes("individual") &&
      !type.includes("private") &&
      !type.includes("1:1");
    return endDate > now && startDate <= now && (isKnownGroupType || isBroadGroupType);
  });

  // Check for bammbuu+ individual (1:1) subscription (allows Individual Premium
  // classes). Mirrors hasGroupSubscription but matches the individual/1:1 plan.
  const hasIndividualSubscription = user?.subscriptions?.some((sub) => {
    if (
      !sub.startDate ||
      !sub.endDate ||
      !sub.type ||
      sub.type.toLowerCase() === "none"
    )
      return false;
    const endDate = parseSubDate(sub.endDate);
    const startDate = parseSubDate(sub.startDate);
    if (!endDate || !startDate) return false;
    const now = new Date();
    const type = sub.type.trim().toLowerCase();
    const isKnownIndividualType =
      type === "individual_premium" ||
      type === "bammbuu+ instructor-led 1:1 classes" ||
      type === "bammbuu individual";
    const isBroadIndividualType =
      type.includes("individual") ||
      type.includes("1:1") ||
      type.includes("private");
    return (
      endDate > now &&
      startDate <= now &&
      (isKnownIndividualType || isBroadIndividualType)
    );
  });

  if (contentType === "premium-class") {
    // Handle Group Premium classes
    if (classType === "Group Premium") {
      if (hasExamPrepPlan) {
        return {
          hasAccess: true,
          reason: "Immersive Exam Prep plan allows premium group access",
        };
      }
      if (hasGroupSubscription) {
        return { hasAccess: true, reason: "Valid group subscription" };
      }
      return {
        hasAccess: false,
        reason:
          "Premium group access requires free trial, exam prep plan, group class subscription, or credits",
      };
    }
    // Handle Individual Premium classes
    else if (classType === "Individual Premium") {
      if (hasExamPrepPlan) {
        return {
          hasAccess: true,
          reason:
            "Immersive Exam Prep plan allows individual premium class access",
        };
      }
      if (hasIndividualSubscription) {
        return { hasAccess: true, reason: "Valid individual subscription" };
      }
      return {
        hasAccess: false,
        reason:
          "Individual premium classes require free trial, credits, an individual subscription, or Immersive Exam Prep plan",
      };
    }
  }

  // Handle standard content
  if (!contentType || contentType === "standard") {
    return { hasAccess: true, reason: "Standard content" };
  }

  return { hasAccess: false, reason: "User does not have the required access" };
};

/**
 * Check if a premium one-time class should be hidden from non-members
 * @param {Object} classData - The class data object
 * @param {Object} user - The current user object
 * @returns {boolean} - True if the class should be hidden
 */
export const shouldHidePremiumOneTimeClass = (classData, user) => {
  // Only apply this rule to premium classes
  const isPremium =
    classData.classType === "Individual Premium" ||
    classData.classType === "Group Premium";
  if (!isPremium) {
    return false;
  }

  // Only apply to one-time classes
  const isOneTime =
    classData.recurrenceTypes &&
    classData.recurrenceTypes.length > 0 &&
    classData.recurrenceTypes[0] === "One-time";
  if (!isOneTime) {
    return false;
  }

  // If user is a member of the class, don't hide it
  if (
    user &&
    classData.classMemberIds &&
    classData.classMemberIds.includes(user.uid)
  ) {
    return false;
  }

  // If user is the admin/tutor of the class, don't hide it
  if (
    user &&
    (classData.adminId === user.uid || classData.tutorId === user.uid)
  ) {
    return false;
  }

  // Check if class is within 12 hours of starting
  if (!classData.classDateTime || !classData.classDateTime.seconds) {
    return false;
  }

  const classStartTime = new Date(classData.classDateTime.seconds * 1000);
  const now = new Date();
  const twelveHoursBeforeClass = new Date(
    classStartTime.getTime() - 12 * 60 * 60 * 1000,
  );

  // Hide the class if we're within 12 hours of it starting
  return now >= twelveHoursBeforeClass;
};
